import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import _get from 'lodash/get';

import { useKeyPress, useMedia } from 'bdvp-common/src/hooks';
import { keyConstants } from 'bdvp-common/src/utils';
import { constants as modalConstants } from 'bdvp-graphql/src/resolvers/modals';

import Lightbox from '../Lightbox';
import LightboxUtils from '../Lightbox/LightboxUtils';
import PreviousNextButton from '../Lightbox/PreviousNextButton';
import { ModalWrapperOverride } from './LightboxModal.style';

import BREAKPOINTS from '@mlb/react-patterns/style/breakpoints.ts';

const { LIGHTBOX_MODAL_NAME } = modalConstants;
const { TAB } = keyConstants;
const keysToTrack = [TAB];

const ButtonsWrapper = styled.div`
    position: absolute;
    top: 10px;
    left: 10px;
`;
const LightboxModal = ({
    onClosedHandler,
    onOpenedHandler,
    clipIndex,
    searchResultsList,
    fetchMoreData,
    totalResults,
    lang,
    playerConfig,
    isShareModalOpen,
    closeLightbox,
    dictionary,
    appView,
    setClipIndex,
}) => {

    const closeButtonRef = React.useRef(null);
    const shareButtonRef = React.useRef(null);

    const playerWrapperRef = React.useRef(null);
    const clipIndexRef = React.useRef(clipIndex);

    const mediaPlaybackRef = React.useRef(null);

    useEffect(() => {

        clipIndexRef.current = clipIndex;

    }, [clipIndex]);

    const areButtonsInHeader = useMedia(
        [`(min-width: ${BREAKPOINTS.md}) and (orientation: landscape)`],
        [false],
        true
    );

    let mediaPlayback = React.useMemo(() => _get(searchResultsList, `[${clipIndex}].mediaPlayback[0]`, {}), [
        searchResultsList,
        clipIndex,
    ]);

    useEffect(() => {

        if (clipIndex > searchResultsList.length) {

            fetchMoreData();

        }

    }, [
        searchResultsList,
        clipIndex,
        fetchMoreData,
    ]);

    useEffect(() => {

        mediaPlaybackRef.current = mediaPlayback;

    });

    const keyPressCallbackProps = {
        cb: (event) => {

            if (event.target === shareButtonRef.current) {

                if (!closeButtonRef.current) return;
                event.preventDefault();
                closeButtonRef.current.focus();

            }

        },
        shiftCB: (event) => {

            if (event.target === closeButtonRef.current) {

                if (!shareButtonRef.current) return;
                event.preventDefault();
                shareButtonRef.current.focus();

            }

        },
    };

    useKeyPress(keysToTrack, keyPressCallbackProps);

    const isPrerollAdEnabled = _get(playerConfig, 'mediaConfig.videocore.advertising.dfp.enabled');

    const onOpenedLightboxHandler = () => {

        if (closeButtonRef.current) {

            closeButtonRef.current.focus();

        }
        onOpenedHandler();

    };

    const handlers = {
        onClosed: onClosedHandler,
        onOpened: onOpenedLightboxHandler,
    };

    const onPreviousNextBtnClick = (type) => {

        const updatedIndex = LightboxUtils.getClipIndex(type, clipIndexRef.current, searchResultsList);
        setClipIndex(updatedIndex);
        LightboxUtils.preloadData(updatedIndex, totalResults, searchResultsList, fetchMoreData);
        LightboxUtils.resetVideoPlayback(playerWrapperRef.current, isPrerollAdEnabled);

    };

    const previousButtonHandler = () => {

        onPreviousNextBtnClick('previous');

    };

    const nextButtonHandler = () => {

        onPreviousNextBtnClick('next');

    };

    const previousButton = (
        <PreviousNextButton
            ariaLabel={'previous'}
            disabled={clipIndex === 0}
            iconName={'arrow-previous'}
            left={true}
            onClickHandler={previousButtonHandler}
        />
    );

    const nextButton = (
        <PreviousNextButton
            ariaLabel={'next'}
            iconName={'arrow-next'}
            disabled={totalResults ===  clipIndex + 1}
            onClickHandler={nextButtonHandler}
            right={true}
            isNextButton={true}
        />
    );

    const headerButton = areButtonsInHeader && (
        <ButtonsWrapper>{previousButton}{nextButton}</ButtonsWrapper>
    );

    return (
        <ModalWrapperOverride
            name={LIGHTBOX_MODAL_NAME}
            width={'100%'}
            height={'100%'}
            overlay={true}
            overlayOpacity={0.9}
            handlers={handlers}
            closeButtonRef={closeButtonRef}
            {...(headerButton && { headerButton })}
        >
            <Lightbox
                appView={appView}
                closeLightbox={closeLightbox}
                dictionary={dictionary}
                isShareModalOpen={isShareModalOpen}
                lang={lang}
                mediaPlayback={mediaPlayback}
                mediaPlaybackRef={mediaPlaybackRef}
                onPreviousNextBtnClick={onPreviousNextBtnClick}
                playerConfig={playerConfig}
                playerWrapperRef={playerWrapperRef}
                shareButtonRef={shareButtonRef}
                searchResultsList={searchResultsList}
                {...(!areButtonsInHeader ? {
                    previousButton,
                    nextButton,
                }: {}) }
            />
        </ModalWrapperOverride>
    );

};

LightboxModal.propTypes = {
    onClosedHandler: PropTypes.func.isRequired,
    onOpenedHandler: PropTypes.func.isRequired,
    clipIndex: PropTypes.number.isRequired,
    searchResultsList: PropTypes.array.isRequired,
    fetchMoreData: PropTypes.func.isRequired,
    totalResults: PropTypes.number.isRequired,
    lang: PropTypes.string.isRequired,
    playerConfig: PropTypes.shape().isRequired,
    isShareModalOpen: PropTypes.bool.isRequired,
    closeLightbox: PropTypes.func.isRequired,
    appView: PropTypes.string.isRequired,
};

export default LightboxModal;
